import React, { useContext, useEffect } from 'react';
import I18n from 'i18n-js';
import { gql, useApolloClient } from '@apollo/client';
import { RCButton, RCIconButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { TrashIcon } from '@reverbdotcom/cadence/icons/react';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { CoreListingDeleteDraft } from '@reverbdotcom/commons/src/rql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingCardContext from '../SellerListingCardContext';
import BulkSelectContext from '../BulkSelectContext';
import { ActionType as BulkSelectActionType } from '../bulkSelectReducer';

interface IListingRowDeleteButtonProps {
  setDraftDeleteErrors: React.Dispatch<React.SetStateAction<Array<string>>>
}

export const COMPONENT_NAME = 'ListingRowDeleteButton';

function ListingRowDeleteButton({ setDraftDeleteErrors }: IListingRowDeleteButtonProps) {
  const apolloClient = useApolloClient();
  const [
    deleteDraftListing,
    {
      data: mutationData,
      loading: mutationDataLoading,
      errors: mutationErrors,
    },
  ] =
    useMutation<
      CoreListingDeleteDraft.Mutation,
      CoreListingDeleteDraft.Variables
    >(DELETE_DRAFT_MUTATION);

  const { dispatch: selectionDispatch } = useContext(BulkSelectContext);
  const { cardState, dispatch } = useContext(SellerListingCardContext);

  useEffect(() => {
    dispatch({
      type: 'setLoadingListing',
      payload: {
        loadingListing: mutationDataLoading,
      },
    });

    const { isFinishedLoading, hasData, hasErrors } = checkResponse(
      mutationDataLoading,
      mutationData,
      mutationErrors,
    );

    const hasAPIResponse = isFinishedLoading && (hasData || hasErrors);

    if (!hasAPIResponse) return;

    if (hasErrors) {
      handleErrors();
    } else {
      selectionDispatch({
        type: BulkSelectActionType.REMOVE_SELECTED_LISTING,
        payload: {
          listing: cardState.listing,
        },
      });

      selectionDispatch({
        type: BulkSelectActionType.REMOVE_SELECTABLE_LISTING,
        payload: {
          listing: cardState.listing,
        },
      });
      evictFromCache();
    }
  }, [mutationDataLoading]);

  async function startDelete() {
    const confirmMessage = I18n.t(
      'discovery.listingsManagement.sellerListingCard.deleteDraftConfirm',
      {
        title: cardState.listing.title,
      },
    );

    if (!confirm(confirmMessage)) return;

    await deleteDraftListing({
      variables: {
        input: {
          listingId: cardState.listing.id,
        },
      },
    });
  }

  function checkResponse(
    dataLoading: boolean,
    data: unknown,
    errors: string | any[],
  ) {
    return {
      isFinishedLoading: !dataLoading,
      hasData: !!data,
      hasErrors: !!errors?.length,
    };
  }

  function handleErrors() {
    const [rawErrors] = mutationErrors;
    const parsedErrors = JSON.parse(rawErrors.message);
    setDraftDeleteErrors(parsedErrors);
  }

  function evictFromCache() {
    apolloClient.cache.evict({
      id: apolloClient.cache.identify({
        __typename: 'Listing',
        id: cardState.listing.id,
      }),
    });
    apolloClient.cache.gc();
  }

  function handleClick() {
    startDelete();
    trackEvent({
      eventName: MParticleEventName.ClickedComponent,
      componentName: COMPONENT_NAME,
      listingId: cardState.listing.id,
    });
  }

  return (
    <>
      <div className="tablet-d-none">
        <RCButton onClick={handleClick} variant="tertiary" fullWidth>
          <RCTextWithIcon svgComponent={TrashIcon} placement="right">
            {I18n.t('discovery.listingsManagement.sellerListingCard.deleteDraft')}
          </RCTextWithIcon>
        </RCButton>
      </div>
      <div className="seller-listing-card-footer__tablet-button-container">
        <RCIconButton onClick={handleClick} label="Delete Listing" svgComponent={TrashIcon} variant="muted" />
      </div>
    </>
  );
}

const DELETE_DRAFT_MUTATION = gql`
  mutation Core_Listing_DeleteDraft(
    $input: Input_core_apimessages_DraftListingDeleteRequest
  ) {
    deleteDraftListing(input: $input) {
      listingId
    }
  }
`;

export default ListingRowDeleteButton;
